package features.schedule.ui

import kotlinx.html.*
import kotlinx.html.js.onClickFunction
import kotlinx.html.js.onChangeFunction
import org.w3c.dom.HTMLSelectElement
import features.teams.domain.Team
import AppState
import Router
import features.schedule.handlers.bulkAssignShift
import features.schedule.handlers.handleScheduleCellClick



object ScheduleTab {
    fun render(parent: DIV, team: Team) {
        val members = AppState.members[team.id] ?: emptyList()
        val schedule = AppState.schedules[team.id]
        val shifts = AppState.shiftTemplates[team.id] ?: emptyList()
        
        // Date Filter
        val currentMonth = AppState.scheduleMonth
        val currentYear = AppState.scheduleYear
        val daysInMonth = js("new Date(currentYear, currentMonth, 0).getDate()") as Int
        
        val selectedCount = AppState.selectedScheduleCells.size
        val isOwner = team.role == "owner"
        val isArchived = team.isArchived
        
        parent.div {
            // Filter Bar
            div("flex items-center gap-4 mb-6") {
                div("flex items-center gap-2") {
                    label("text-sm text-slate-400") { +"Month:" }
                    select("bg-dark-700 border border-dark-600 text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-brand-teal transition") {
                        val months = listOf("January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December")
                        months.forEachIndexed { index, month ->
                            option {
                                value = (index + 1).toString()
                                selected = (index + 1) == currentMonth
                                +month
                            }
                        }
                        onChangeFunction = { event ->
                            val target = event.target as? HTMLSelectElement
                            if (target != null) {
                                val newMonth = target.value.toIntOrNull() ?: currentMonth
                                AppState.scheduleMonth = newMonth
                                
                                // Reload schedule data for new month
                                val teamId = team.id
                                features.schedule.data.ScheduleRepository.loadSchedule(teamId, AppState.scheduleYear, newMonth)
                                    .then { schedule ->
                                        if (schedule != null) {
                                            AppState.schedules[teamId] = schedule
                                        }
                                        Router.render()
                                    }
                                    .catch { error ->
                                        console.error("Failed to load schedule:", error)
                                        Router.render()
                                    }
                            }
                        }
                    }
                }
                
                div("flex items-center gap-2") {
                    label("text-sm text-slate-400") { +"Year:" }
                    select("bg-dark-700 border border-dark-600 text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-brand-teal transition") {
                        val baseYear = 2025
                        for (year in baseYear..(baseYear + 2)) {
                            option {
                                value = year.toString()
                                selected = year == currentYear
                                +year.toString()
                            }
                        }
                        onChangeFunction = { event ->
                            val target = event.target as? HTMLSelectElement
                            if (target != null) {
                                val newYear = target.value.toIntOrNull() ?: currentYear
                                AppState.scheduleYear = newYear
                                
                                // Reload schedule data for new year
                                val teamId = team.id
                                features.schedule.data.ScheduleRepository.loadSchedule(teamId, newYear, AppState.scheduleMonth)
                                    .then { schedule ->
                                        if (schedule != null) {
                                            AppState.schedules[teamId] = schedule
                                        }
                                        Router.render()
                                    }
                                    .catch { error ->
                                        console.error("Failed to load schedule:", error)
                                        Router.render()
                                    }
                            }
                        }
                    }
                }
            }

            // Info Banner (always shown for owners)
            if (isOwner && !isArchived) {
                div("bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 mb-4") {
                    div("flex items-center gap-2 text-sm") {
                        span("text-amber-400 text-lg") { +"💡" }
                        span("text-amber-200") { 
                            +"Tip: Click cells to select, "
                            span("font-bold") { +"Shift+Click" }
                            +" for range selection. Then assign shifts using the buttons above."
                        }
                    }
                }
            }
            
            // Selection Toolbar (shown when cells are selected)
            if (selectedCount > 0 && isOwner && !isArchived) {
                div("bg-dark-800 border border-dark-700 rounded-xl p-4 mb-4") {
                    div("flex items-center justify-between gap-4") {
                        div("flex items-center gap-3") {
                            span("text-sm text-slate-400") { +"$selectedCount cells selected" }
                            button(classes = "text-xs text-slate-500 hover:text-white transition") {
                                onClickFunction = { 
                                    AppState.selectedScheduleCells.clear()
                                    AppState.lastSelectedCell = null
                                    Router.render()
                                }
                                +"Clear Selection"
                            }
                        }
                        
                        // Shift template buttons
                        div("flex gap-2 items-center") {
                            span("text-sm text-slate-400 mr-2") { +"Assign Shift:" }
                            shifts.forEach { shift ->
                                button(classes = "px-3 py-1.5 rounded-lg text-xs font-bold transition hover:scale-105") {
                                    style = "background-color: ${shift.color}; color: white"
                                    onClickFunction = { bulkAssignShift(shift.code) }
                                    +shift.code
                                }
                            }
                            // Clear button
                            button(classes = "px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-700 hover:bg-slate-600 text-white transition") {
                                onClickFunction = { bulkAssignShift("-") }
                                +"Clear"
                            }
                        }
                    }
                }
            }
            
            div("overflow-x-auto pb-4 custom-scrollbar") {
                if (schedule == null) {
                    p("text-slate-400 text-center py-8") { +"No schedule data available" }
                    return@div
                }
                
                table("w-full border-collapse min-w-[800px] text-sm text-left") {
                    thead {
                        tr {
                            th(classes = "p-3 border-b border-dark-700 text-slate-400 font-medium sticky left-0 bg-dark-800 z-10") { +"Member" }
                            for (day in 1..daysInMonth) {
                                th(classes = "p-3 border-b border-dark-700 text-slate-400 font-medium text-center") {
                                    +day.toString()
                                }
                            }
                        }
                    }
                    tbody {
                        members.forEach { member ->
                            tr("hover:bg-dark-700/30 transition") {
                                td(classes = "p-3 border-b border-dark-700 font-medium text-white sticky left-0 bg-dark-800 z-10") {
                                    +member.name
                                }
                                
                                for (day in 1..daysInMonth) {
                                    val cellId = "${member.id}:$day"
                                    val isSelected = AppState.selectedScheduleCells.contains(cellId)
                                    val shiftCode = schedule.assignments[member.id]?.get(day) ?: "-"
                                    val shift = shifts.find { it.code == shiftCode }
                                    val color = shift?.color ?: "#334155" // Slate-700
                                    val textColor = if (shift != null) "white" else "#1e293b" // slate-900
                                    
                                    td(classes = "p-2 border-b border-dark-700 text-center cursor-pointer hover:bg-dark-700/50 ${if (isSelected) "ring-2 ring-brand-cyan ring-inset" else ""}") {
                                        attributes["data-cell-id"] = cellId
                                        attributes["data-member-id"] = member.id
                                        attributes["data-day"] = day.toString()
                                        
                                        onClickFunction = { event ->
                                            if (isOwner && !isArchived) {
                                                handleScheduleCellClick(event, member.id, day)
                                            }
                                        }
                                        
                                        span(classes = "inline-block px-2 py-1 rounded-md text-xs font-bold shadow-sm") {
                                            style = "background: $color; color: $textColor"
                                            +shiftCode
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
